import { useEffect, useRef } from 'react'
import { CheckIcon } from '../icons'
import { cn, cva } from '../lib/cva'
import { Label } from './Label'
import { Text } from './Text'

const tabVariants = cva(
	'flex px-6 py-6 cursor-pointer transition-colors relative text-neutral-800 hover:text-neutral-800 focus:outline-none',
	{
		variants: {
			disabled: {
				true: 'text-neutral-300 cursor-not-allowed hover:text-neutral-300',
				false: '',
			},
		},
		defaultVariants: {
			disabled: false,
		},
	},
)

const indicatorVariants = cva('absolute bottom-0 left-0 w-full h-1 transition-colors', {
	variants: {
		selected: {
			true: 'bg-primary-600',
			false: 'bg-transparent group-hover:bg-primary-600',
		},
		disabled: {
			true: 'bg-neutral-300 group-hover:bg-neutral-300',
			false: '',
		},
	},
	defaultVariants: {
		selected: false,
		disabled: false,
	},
})

const iconContainerVariants = cva('inline-flex items-center justify-center w-6 h-6 rounded-full mr-2', {
	variants: {
		iconType: {
			number: 'bg-neutral-500 text-neutral-0',
			check: 'bg-primary-600 text-neutral-0',
		},
		selected: {
			true: 'bg-primary-600 text-neutral-0',
			false: '',
		},
		disabled: {
			true: 'bg-neutral-300 text-neutral-0 group-hover:bg-neutral-300',
			false: 'group-hover:bg-primary-600 group-hover:text-neutral-0',
		},
	},
	defaultVariants: {
		selected: false,
		disabled: false,
	},
})

export type TabIconType = 'number' | 'check' | null

export interface TabItem<IdType = string> {
	id: IdType
	label: string
	iconType?: TabIconType
	iconValue?: number | string
	badge?: number | string
	/**
	 * Visual treatment for the badge pill. 'default' is neutral gray (used as
	 * an info count); 'red' is the notification/error treatment for things the
	 * user should attend to (e.g. pending HR review flags).
	 */
	badgeColor?: 'default' | 'red'
	disabled?: boolean
}

export interface TabsProps<IdType> {
	tabs: TabItem<IdType>[]
	activeTab: string
	onChange?: (tabId: IdType) => void
	className?: string
	'aria-label'?: string
}

export function TabBar<IdType = string>({
	tabs,
	activeTab = '',
	onChange,
	className,
	'aria-label': ariaLabel = 'Tabs',
}: TabsProps<IdType>) {
	const containerRef = useRef<HTMLDivElement>(null)

	// biome-ignore lint/correctness/useExhaustiveDependencies: activeTab is a prop — changing it should re-run the scroll
	useEffect(() => {
		const container = containerRef.current
		if (!container) return
		const active = container.querySelector<HTMLElement>('[aria-selected="true"]')
		active?.scrollIntoView({ block: 'nearest', inline: 'center' })
	}, [activeTab])

	const handleTabClick = (tab: TabItem<IdType>) => {
		if (tab.disabled) return
		if (onChange) {
			onChange(tab.id)
		}
	}

	const renderIcon = (tab: TabItem<IdType>, isSelected: boolean) => {
		if (!tab.iconType) return null

		return (
			<span
				className={iconContainerVariants({
					iconType: tab.iconType,
					selected: isSelected,
					disabled: tab.disabled,
				})}>
				{tab.iconType === 'check' ? (
					<CheckIcon size={18} className='fill-current' />
				) : (
					<Text variant='hint'>{tab.iconValue || tab.label}</Text>
				)}
			</span>
		)
	}

	return (
		<div
			ref={containerRef}
			className={cn('flex overflow-x-auto scrollbar-hide border-b border-neutral-100', className)}
			role='tablist'
			aria-label={ariaLabel}>
			{tabs.map((tab) => {
				const isSelected = tab.id === activeTab
				return (
					<button
						key={tab.label}
						type='button'
						className={cn('group flex flex-shrink-0 items-center', tabVariants({ disabled: tab.disabled }))}
						role='tab'
						aria-selected={isSelected}
						aria-disabled={tab.disabled}
						id={`tab-${tab.id}`}
						onClick={() => handleTabClick(tab)}
						disabled={tab.disabled}>
						{renderIcon(tab, isSelected)}
						<Text variant='xsmall' weight={isSelected ? 'semibold' : 'regular'}>
							{tab.label}
						</Text>
						{tab.badge !== undefined &&
							tab.badge !== null &&
							(tab.badgeColor === 'red' ? (
								<span className='ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-error-600 text-neutral-0 text-[10px] font-semibold leading-none'>
									{tab.badge}
								</span>
							) : (
								<Label variant='solid' color='default' className='ml-2'>
									{tab.badge}
								</Label>
							))}
						<div className={indicatorVariants({ selected: isSelected, disabled: tab.disabled })} />
					</button>
				)
			})}
		</div>
	)
}
